import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Alert, App, Button, Card, Form, Input, Typography } from 'antd';
import { LockOutlined, UserOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';

import { login as loginRequest } from '@/api/auth';
import { extractApiErrorMessage } from '@/api/client';
import { decodeJwt } from '@/lib/jwt';
import { useAuthStore } from '@/stores/authStore';
import type { LoginPayload, LoginResponse } from '@/types/auth';
import './login-page.css';

const { Title, Text, Paragraph } = Typography;

const loginSchema = z.object({
  username: z
    .string()
    .min(1, 'Vui lòng nhập email hoặc số điện thoại')
    .max(120, 'Tên đăng nhập quá dài'),
  password: z
    .string()
    .min(1, 'Vui lòng nhập mật khẩu')
    .max(128, 'Mật khẩu quá dài'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

interface LocationState {
  from?: { pathname: string };
}

export function LoginPage() {
  const [form] = Form.useForm<LoginFormValues>();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((s) => s.setSession);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());

  const [formError, setFormError] = useState<string | null>(null);

  const redirectTo =
    (location.state as LocationState | null)?.from?.pathname ?? '/';

  const mutation = useMutation<LoginResponse, unknown, LoginPayload>({
    mutationFn: loginRequest,
    onSuccess: (data) => {
      const payload = decodeJwt(data.token);
      if (!payload) {
        setFormError('Token không hợp lệ. Vui lòng thử lại.');
        return;
      }
      if (payload.role !== 'ADMIN') {
        setFormError(
          'Tài khoản này không có quyền truy cập trang quản trị. Vui lòng dùng tài khoản ADMIN.',
        );
        return;
      }
      setSession(data);
      message.success('Đăng nhập thành công');
      navigate(redirectTo, { replace: true });
    },
    onError: (error) => {
      setFormError(
        extractApiErrorMessage(
          error,
          'Đăng nhập thất bại. Vui lòng kiểm tra lại thông tin.',
        ),
      );
    },
  });

  if (isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  const handleSubmit = (values: LoginFormValues) => {
    setFormError(null);
    const parsed = loginSchema.safeParse(values);
    if (!parsed.success) {
      const first = parsed.error.errors[0]?.message ?? 'Dữ liệu không hợp lệ';
      setFormError(first);
      return;
    }
    mutation.mutate(parsed.data);
  };

  return (
    <div className="login-shell">
      <Card className="login-card" variant="borderless">
        <div className="login-brand">
          <span className="login-brand-dot" />
          <Text strong>dh3g.vn admin</Text>
        </div>

        <Title level={3} style={{ marginTop: 8, marginBottom: 4 }}>
          Đăng nhập quản trị
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 24 }}>
          Đăng nhập bằng tài khoản đã được cấp quyền quản trị dh3g.vn.
        </Paragraph>

        {formError ? (
          <Alert
            type="error"
            showIcon
            message={formError}
            style={{ marginBottom: 16 }}
            closable
            onClose={() => setFormError(null)}
          />
        ) : null}

        <Form<LoginFormValues>
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={handleSubmit}
          disabled={mutation.isPending}
          autoComplete="on"
        >
          <Form.Item
            label="Email hoặc số điện thoại"
            name="username"
            rules={[{ required: true, message: 'Vui lòng nhập email hoặc số điện thoại' }]}
          >
            <Input
              size="large"
              prefix={<UserOutlined />}
              placeholder="admin@dh3g.vn"
              autoComplete="username"
              autoFocus
            />
          </Form.Item>

          <Form.Item
            label="Mật khẩu"
            name="password"
            rules={[{ required: true, message: 'Vui lòng nhập mật khẩu' }]}
          >
            <Input.Password
              size="large"
              prefix={<LockOutlined />}
              placeholder="••••••••"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 8 }}>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={mutation.isPending}
            >
              Đăng nhập
            </Button>
          </Form.Item>
        </Form>

        <Paragraph type="secondary" style={{ marginTop: 24, marginBottom: 0, fontSize: 12 }}>
          Cần trợ giúp? Liên hệ quản trị viên dh3g.vn. Quay lại{' '}
          <Link to="/">trang chính</Link>.
        </Paragraph>
      </Card>
    </div>
  );
}
